import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Topbar from "../components/Topbar";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Plus,
  BookMarked,
  Search,
  Loader2,
  Pencil,
} from "lucide-react";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import ChipGroup from "../components/ChipGroup";
import { areaIcon, areaLabel } from "../lib/courseAreas";

function NewCourseDialog({ open, onOpenChange, onCreated, areaOptions }) {
  const [form, setForm] = useState({ title: "", description: "", area: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ title: "", description: "", area: "" });
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.area.trim()) {
      toast.error("Indica l'area del corso");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/courses", {
        title: form.title,
        description: form.description,
        area: form.area.trim(),
      });
      toast.success("Corso creato");
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-course-dialog" className="max-w-lg rounded-sm">
        <DialogHeader>
          <DialogTitle className="wordmark text-brand-blue">Nuovo corso</DialogTitle>
          <DialogDescription>Crea un nuovo percorso formativo. I moduli e le lezioni si aggiungono nella pagina successiva.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Titolo</Label>
            <Input
              data-testid="new-course-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1.5 h-10 rounded-sm"
              placeholder="Es. Introduzione alle Gare d'Appalto"
              required
            />
          </div>
          <div>
            <Label>Descrizione</Label>
            <textarea
              data-testid="new-course-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1.5 w-full min-h-[90px] rounded-sm border border-brand-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
              placeholder="Breve descrizione del corso…"
            />
          </div>
          <div>
            <Label>Area</Label>
            <Input
              data-testid="new-course-area"
              list="new-course-area-options"
              value={form.area}
              onChange={(e) => setForm({ ...form, area: e.target.value })}
              className="mt-1.5 h-10 rounded-sm"
              placeholder="Scegli o crea un'area"
              maxLength={60}
              required
            />
            <datalist id="new-course-area-options">
              {(areaOptions || []).map((a) => (
                <option key={a} value={a} />
              ))}
            </datalist>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} className="px-4 py-2 text-sm text-brand-muted">
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="new-course-submit"
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              Crea corso
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_OPTIONS = [
  { value: "all", label: "Tutti gli stati" },
  { value: "yes", label: "Pubblicati" },
  { value: "no", label: "Bozza" },
];

/**
 * Reusable courses list page for formatore ("I miei corsi") and super_admin ("Tutti i corsi").
 */
export default function CoursesList({ scope = "own" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ area: "all", published: "all", search: "" });
  const [areaOptions, setAreaOptions] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const navigate = useNavigate();

  // Aree in uso → chip di filtro + suggerimenti nel dialog "Nuovo corso".
  const loadAreas = useCallback(() => {
    api.get("/courses/areas").then((r) => setAreaOptions(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { loadAreas(); }, [loadAreas]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.area !== "all") params.area = filters.area;
      if (filters.published !== "all") params.is_published = filters.published === "yes";
      if (filters.search.trim()) params.search = filters.search.trim();
      const { data } = await api.get("/courses", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const isAdmin = scope === "admin";
  const areaChipOptions = [
    { value: "all", label: "Tutte le aree" },
    ...areaOptions.map((a) => ({ value: a, label: a })),
  ];

  return (
    <div data-testid={isAdmin ? "admin-courses-page" : "formatore-dashboard"} className="min-h-screen bg-brand-bg">
      <Topbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-gold mb-2">
              <BookMarked size={14} /> {isAdmin ? "Pannello Super Admin" : "Area Formatore"}
            </div>
            <h1 className="text-3xl sm:text-4xl text-brand-ink mb-1">
              {isAdmin ? "Tutti i corsi" : "I miei corsi"}
            </h1>
            <p className="text-sm text-brand-muted">
              {items.length} cors{items.length === 1 ? "o" : "i"} disponibil{items.length === 1 ? "e" : "i"}
            </p>
          </div>
          <button
            onClick={() => setOpenNew(true)}
            data-testid="new-course-btn"
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus size={16} /> Nuovo corso
          </button>
        </div>

        {!isAdmin && (
          <div className="flex gap-1 mb-6 border-b border-brand-line">
            <span className="px-3 py-2 text-sm font-semibold text-brand-blue border-b-2 border-brand-blue -mb-px">I miei corsi</span>
            <Link to="/formatore/provvigioni" className="px-3 py-2 text-sm font-semibold text-brand-muted hover:text-brand-blue">Estratto conto</Link>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-brand-line rounded-sm p-4 mb-6 space-y-4">
          <div className="relative max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
            <Input
              data-testid="courses-search"
              placeholder="Cerca per titolo…"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-9 h-10 rounded-sm"
            />
          </div>
          {areaOptions.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-brand-muted mb-2">Area</div>
              <ChipGroup
                testid="courses-filter-area"
                ariaLabel="Filtra per area"
                options={areaChipOptions}
                value={filters.area}
                onChange={(v) => setFilters({ ...filters, area: v })}
              />
            </div>
          )}
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-brand-muted mb-2">Stato</div>
            <ChipGroup
              testid="courses-filter-status"
              ariaLabel="Filtra per stato"
              options={STATUS_OPTIONS}
              value={filters.published}
              onChange={(v) => setFilters({ ...filters, published: v })}
            />
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-center py-16 text-brand-muted">
            <Loader2 size={20} className="animate-spin inline mr-2" /> Caricamento corsi…
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-dashed border-brand-line rounded-sm p-16 text-center">
            <BookMarked size={28} className="mx-auto text-brand-blue mb-3" />
            <div className="text-lg text-brand-ink mb-1">Nessun corso disponibile</div>
            <p className="text-sm text-brand-muted">Inizia creando il tuo primo corso.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((c) => {
              const Icon = areaIcon(c.area);
              return (
                <div
                  key={c.id}
                  data-testid={`course-card-${c.id}`}
                  className="bg-white border border-brand-line rounded-sm p-6 hover:border-brand-blue/40 hover:shadow-md transition-[box-shadow,border-color] duration-200"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-sm bg-brand-blue/5 text-brand-blue">
                      <Icon size={22} strokeWidth={1.75} />
                    </div>
                    <span
                      className={
                        "text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-sm border " +
                        (c.is_published
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-amber-50 text-amber-700 border-amber-200")
                      }
                    >
                      {c.is_published ? "Pubblicato" : "Bozza"}
                    </span>
                  </div>
                  <h3 className="text-lg text-brand-blue mb-1 line-clamp-2">{c.title}</h3>
                  <div className="text-xs text-brand-muted mb-3">
                    {areaLabel(c.area)} · {c.modules_count ?? 0} modul{(c.modules_count ?? 0) === 1 ? "o" : "i"}
                    {isAdmin && c.owner_name ? ` · ${c.owner_name}` : ""}
                  </div>
                  <p className="text-sm text-brand-muted line-clamp-3 mb-5 min-h-[3.5rem]">
                    {c.description || "Nessuna descrizione."}
                  </p>
                  <button
                    data-testid={`course-edit-${c.id}`}
                    onClick={() => navigate(`/corsi/${c.id}/modifica`)}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:underline underline-offset-4"
                  >
                    <Pencil size={14} /> Modifica corso
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <NewCourseDialog
        open={openNew}
        onOpenChange={setOpenNew}
        areaOptions={areaOptions}
        onCreated={(c) => navigate(`/corsi/${c.id}/modifica`)}
      />
    </div>
  );
}
